import React, { useEffect, useMemo, useState } from "react";

import { Box, Checkbox, Stack, Typography } from "@mui/material";

import { Alert, Button, FieldLabel, Input, Modal } from "@repo/ui";

import { FileUploadField } from "components/input/fields/FileUploadField";
import {
  useFontRightsWording,
  useUploadCompanyFont,
} from "hooks/api/useCompanyFonts";

interface UploadFontModalProps {
  open: boolean;
  onClose: () => void;
  companyId?: string;
  /** Fired with the new font's URL so the caller can select it immediately. */
  onUploaded?: (fileUrl: string, familyLabel: string) => void;
}

/**
 * Upload a brand's own font for use in Studio (VRG-348).
 *
 * WHY A RIGHTS CHECKBOX AT ALL. Serving a font file to consumers is a licensing
 * act, and most foundry DESKTOP licences specifically forbid web embedding — the
 * exact thing this upload does. An upload box with no acknowledgement is an
 * invitation to breach a licence the brand may not have read. Webflow, Squarespace
 * and Canva all gate font upload this way; it is the established pattern for this
 * feature, not a precaution invented here.
 *
 * WHY IT IS A DISABLED SAVE AND NOT A CONFIRM MODAL (§43). The two instruments
 * answer different questions. A confirm modal is for a CONSEQUENCE — something
 * downstream or hard to reverse. Nothing irreversible happens at upload: the font
 * is not applied to anything until the brand selects it, and it can be removed.
 * What this needs is a VALIDITY gate — the form is incomplete until a claim has
 * been made — and a disabled submit is exactly that instrument.
 *
 * WHY THE WORDING COMES FROM THE SERVER. The stored record includes a wording
 * VERSION, whose entire purpose is to answer "what did this brand actually agree
 * to?" after the sentence is next edited. A hardcoded string here would keep
 * rendering the old sentence after the server constant was bumped, and the record
 * would attest to text nobody was ever shown — which is worse than not recording a
 * version at all, because it would look authoritative.
 */
export const UploadFontModal = ({
  open,
  onClose,
  companyId,
  onUploaded,
}: UploadFontModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [familyLabel, setFamilyLabel] = useState("");
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: rights } = useFontRightsWording();
  const uploadFont = useUploadCompanyFont();

  // Every field resets on close, including the checkbox. A rights claim is made
  // per font (§ the table comment) — carrying a tick over to the next upload
  // would produce a record for a file whose licence was never considered.
  useEffect(() => {
    if (!open) {
      setFile(null);
      setFamilyLabel("");
      setRightsConfirmed(false);
      setError(null);
    }
  }, [open]);

  const acceptFiles = useMemo(() => {
    const extensions = rights?.accept_extensions ?? [".woff2", ".otf", ".ttf"];
    // The dropzone keys on MIME type, but browsers are famously inconsistent
    // about font types (`font/otf`, `application/x-font-opentype`, or
    // `application/octet-stream`), so the EXTENSIONS are what actually match
    // here. The server does not trust either — it identifies the file by its
    // leading bytes.
    return { "font/*": extensions } as Record<string, string[]>;
  }, [rights]);

  const canSubmit = !!file && rightsConfirmed && !!companyId;

  const handleSubmit = async () => {
    if (!canSubmit || !file || !companyId) return;
    setError(null);
    try {
      const font = await uploadFont.mutateAsync({
        companyId,
        file,
        familyLabel: familyLabel.trim() || undefined,
        rightsConfirmed,
      });
      onUploaded?.(font.file_url, font.family_label);
      onClose();
    } catch (e: any) {
      setError(
        e?.response?.data?.message ??
          e?.message ??
          "That font could not be uploaded.",
      );
    }
  };

  return (
    <Modal
      title="Upload a font"
      open={open}
      onClose={onClose}
      actions={
        <Stack direction="row" spacing={3} alignItems="center">
          <Button
            variant="secondary"
            size="medium"
            onClick={onClose}
            disabled={uploadFont.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={uploadFont.isPending}
          >
            Upload font
          </Button>
        </Stack>
      }
    >
      <Stack spacing={5}>
        <Box>
          <FieldLabel label="Font file" />
          <FileUploadField
            description={`${(rights?.accept_extensions ?? [".woff2", ".otf", ".ttf"]).join(", ")} up to ${rights?.max_size_mb ?? 5}MB`}
            acceptFiles={acceptFiles}
            maxFiles={1}
            maxSizeMb={rights?.max_size_mb ?? 5}
            height={160}
            onFilesChange={(files) => {
              const next = (files as unknown as { file: File }[])?.[0]?.file;
              setFile(next ?? null);
              // Prefill the display name from the filename so the common case
              // needs no typing, but only while the user has not typed their own.
              if (next && !familyLabel) {
                setFamilyLabel(
                  next.name
                    .replace(/\.[^.]+$/, "")
                    .replace(/[_-]+/g, " ")
                    .replace(/([a-z])([A-Z])/g, "$1 $2")
                    .trim(),
                );
              }
            }}
          />
        </Box>

        {/* The shared @repo/ui Input, not a bare <input>: label treatment,
            label-to-control gap and the border/focus ring all come from one
            source (§31), and a hand-rolled field is exactly how a form ends up
            with three different borders. */}
        <Input
          label="Display name"
          labelAdornment="(optional)"
          helperText="What this font is called in your font list."
          placeholder="e.g. Walsheim Medium"
          value={familyLabel}
          onChange={(value) => setFamilyLabel(value.slice(0, 120))}
        />

        {/* Never pre-ticked, and the sentence names the SPECIFIC right — web
            embedding — rather than a generic "I agree to the terms". Web
            embedding is the exact grant a desktop licence lacks, so naming it
            makes the claim falsifiable, which is what makes it mean anything. */}
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Checkbox
            checked={rightsConfirmed}
            onChange={(e) => setRightsConfirmed(e.target.checked)}
            sx={{ p: 0, mt: "1px" }}
          />
          <Typography variant="body3" sx={{ lineHeight: 1.5 }}>
            {rights?.wording ??
              "I confirm this brand holds a licence permitting web embedding of this font."}
          </Typography>
        </Stack>

        {error && <Alert severity="error" description={error} />}
      </Stack>
    </Modal>
  );
};
